import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Rocket, ArrowRight, School, UserPlus, Upload, LineChart, X, Check } from "lucide-react";

const STEPS = [
  { icon: School, to: "/academy", title: "Create your academy", desc: "Set up your academy, then email invitation links to your coaches, S&C, physios, doctors and athletes." },
  { icon: UserPlus, to: "/add-athlete", title: "Add your first athlete", desc: "Name, sport and goal is enough to start building their digital twin." },
  { icon: Upload, to: "/import", title: "Bring in data", desc: "Import history or connect a wearable so scores come from real signals, not guesses." },
  { icon: LineChart, to: "/", title: "Read the twin", desc: "Open an athlete for readiness, sharpness, injury risk, their calendar and the simulator." },
];

export default function GettingStarted({ open, onClose }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const steps = STEPS.filter((s) => !(s.to === "/academy" && user?.in_academy));
  if (!open) return null;
  const go = (to) => { onClose?.(); navigate(to); };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/25 backdrop-blur-sm p-4" data-testid="getting-started">
      <div className="w-full max-w-lg bg-background text-foreground border border-border rounded-2xl overflow-hidden shadow-xl">
        <div className="px-6 py-5 border-b border-border flex items-center gap-3">
          <Rocket className="w-5 h-5" />
          <div>
            <div className="text-[11px] tracking-[0.25em] uppercase font-bold text-muted-foreground">Welcome to Trifit.XPS</div>
            <div className="font-heading text-xl font-black tracking-tight">Get set up in {steps.length} steps</div>
          </div>
          <button onClick={onClose} data-testid="tutorial-close"
            className="ml-auto text-muted-foreground hover:text-foreground transition-colors"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-2">
          {steps.map((s, i) => (
            <button key={i} onClick={() => go(s.to)} data-testid={`tutorial-step-${i}`}
              className="w-full flex items-center gap-4 text-left p-3 border border-border rounded-xl hover:border-foreground hover:bg-surface transition-colors group">
              <span className="font-heading text-lg font-black tabular-nums w-6">{i + 1}</span>
              <s.icon className="w-5 h-5 text-muted-foreground shrink-0" />
              <span className="flex-1 min-w-0">
                <span className="block font-medium">{s.title}</span>
                <span className="block text-xs text-muted-foreground">{s.desc}</span>
              </span>
              <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-foreground group-hover:translate-x-1 transition-all shrink-0" />
            </button>
          ))}
        </div>
        <div className="px-6 py-4 border-t border-border flex items-center gap-3">
          <p className="text-[11px] text-muted-foreground flex-1">You can reopen this anytime from the “Getting started” button.</p>
          <button onClick={onClose} data-testid="tutorial-done"
            className="text-sm px-4 py-2 rounded-lg bg-foreground text-background font-medium hover:bg-neutral-800 transition-colors flex items-center gap-1.5">
            <Check className="w-4 h-4" /> Got it
          </button>
        </div>
      </div>
    </div>
  );
}
